/**
 * PerformanceStatsFiltersPanel - Filters for Performance Stats page (Publisher/Demand views)
 * Shows date range, traffic sources (publisher view), and demand sources (demand view)
 * Contextually enables/disables filters based on view mode
 */

import { FiltersHelper } from "../common/FiltersHelper";
import { TrafficSourceResponseDto, DemandSourcesResponseDto, ResetPageDto } from "../utils/types";
import { PagesHelper } from "../common/PagesHelper";
import StateHelper from "../utils/StateHelper";
import { useRef } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import { ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import InfoIcon from "../components/clinks/info/InfoIcon";

interface PerformanceStatsFiltersPanelProps {
  search: (updateHistory?: boolean) => void;
  page: string;
  currentUser: any;
  trafficSourceResponseDto?: TrafficSourceResponseDto;
  demandSourcesResponseDto?: DemandSourcesResponseDto;
  viewMode?: 'trafficSource' | 'demand';
  onViewModeChange?: (newMode: 'trafficSource' | 'demand') => void;
}

function PerformanceStatsFiltersPanel(props: PerformanceStatsFiltersPanelProps) {
  const { page, currentUser, trafficSourceResponseDto, demandSourcesResponseDto, viewMode = 'trafficSource', onViewModeChange, search } = props;

  const trafficSourcesRef = useRef(null);
  const demandSourcesRef = useRef(null);

  // Get current date mode from state
  const pageState = StateHelper.getPageState(page);
  const dateMode = pageState.dateMode || 'days';

  // Build dynamic time period text based on dateMode
  const timePeriodText = dateMode === 'months' ? 'Month' : dateMode === 'weeks' ? 'Week' : 'Date';

  // Build dynamic average exclusion text based on dateMode
  const avgExclusionText = dateMode === 'months'
    ? 'Averages exclude this month (incomplete data).'
    : dateMode === 'weeks'
    ? 'Averages exclude this week (incomplete data).'
    : 'Averages exclude today (incomplete data).';

  // Reset all filters
  function clearAllFilters() {
    const resetPageDto: ResetPageDto = {
      trafficSourcesRef: trafficSourcesRef,
      countriesRef: null,
      trafficTypesRef: null,
      groupByRef: null,
      placementsRef: null,
      showMonetizableRef: null,
      showWithRevenueRef: null,
      showWithConversionsRef: null,
      productsRef: null,
      campaignsRef: null,
      demandSourcesRef: demandSourcesRef,
      cLinksRef: null,
      queryFlagsRef: null
    };

    PagesHelper.resetFilters(page, resetPageDto, search);
  }

  // Get filter cards - always render both but pass disabled flag
  const dateRangeCard = FiltersHelper.getDateRangeCard(currentUser, page, avgExclusionText);

  // Traffic sources filter (disabled in demand view)
  const trafficSourcesCard = FiltersHelper.getTrafficSourcesCard(
    currentUser,
    page,
    trafficSourceResponseDto,
    trafficSourcesRef,
    viewMode === 'demand' // disabled when in demand view
  );

  // Demand sources filter (disabled in traffic source view)
  const demandSourcesCard = FiltersHelper.getDemandSourcesCardForProductsPage(
    currentUser,
    page,
    demandSourcesRef,
    demandSourcesResponseDto,
    viewMode === 'trafficSource' // disabled when in traffic source view
  );

  return (
    <>
      <Row>
        <Col xl={4} className="text-center">
        </Col>
        <Col xl={4} className="text-center">
          <Button style={{ fontSize: 12 }} size="sm" variant="outline-primary" onClick={clearAllFilters}>
            Reset filters
          </Button>
        </Col>
        <Col xl={4} className="text-center">
        </Col>
      </Row>

      <Row xl={12} lg={4} md={2}>
        {/* View Mode Toggle */}
        {onViewModeChange && (
          <Col xl={3} className="text-center mb-2">
            <div style={{ border: "none", padding: "0" }}>
              <div style={{ fontSize: "100%", marginBottom: "8px" }}>
                Show stats for{' '}
                <InfoIcon>
                  <span>Toggle between Traffic Source view (traffic source performance grouped by Traffic Source → Site → {timePeriodText}) and Demand view (demand source performance grouped by Demand Source → {timePeriodText})</span>
                </InfoIcon>
              </div>
              <ToggleButtonGroup
                type="radio"
                name="viewMode"
                value={viewMode}
                onChange={(value: string) => onViewModeChange(value as 'trafficSource' | 'demand')}
                style={{ width: '100%' }}
              >
                <ToggleButton
                  value="trafficSource"
                  size="sm"
                  className={viewMode === 'trafficSource' ? 'active-toggle-button' : 'inactive-toggle-button'}
                  style={{ fontSize: '11px', width: '50%' }}
                >
                  Traffic Sources
                </ToggleButton>
                <ToggleButton
                  value="demand"
                  size="sm"
                  className={viewMode === 'demand' ? 'active-toggle-button' : 'inactive-toggle-button'}
                  style={{ fontSize: '11px', width: '50%' }}
                >
                  Demands
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
          </Col>
        )}

        {/* Date Range */}
        <Col xl={3} className="text-center mb-2">
          {dateRangeCard}
        </Col>

        {/* Traffic Sources - disabled in demand view */}
        <Col xl={3} className="text-center mb-2">
          {trafficSourcesCard}
        </Col>

        {/* Demand Sources - disabled in publisher view */}
        <Col xl={3} className="text-center mb-2">
          {demandSourcesCard}
        </Col>
      </Row>
    </>
  );
}

export default PerformanceStatsFiltersPanel;
